import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { text } from '../app/i18n';
import { Dialog, DialogActions } from '../design-system/Overlay';
import { FieldInput, inputFieldOf } from './FieldInput';
import { formatDecimal, formatValue, type FieldSummary } from './format';
import { browserDraftStore, draftAge, draftKey, type DraftStore, type EditorDraft } from './line-draft-store';
import {
  columnTotal,
  editorStatus,
  identityParameters,
  instantToZonedInput,
  lineKey,
  lookupLine,
  mergePrefill,
  newLineId,
  prefillParameters,
  prefilledRows,
  referenceValue,
  setAt,
  unitOf,
  valueAt,
  visibleColumns,
  zonedInputToInstant,
  evaluate,
  type EditorLine,
  type EditorStatus,
  type LineEditorColumn,
  type LineEditorSpec,
  type Row,
} from './line-editor-state';
import { shortId, useReferenceLabels } from './reference-labels';
import { useLoad } from './useLoad';
import { useAppServices, type ViewCommand } from './ViewContext';
import './line-editor.css';

/**
 * Saisie d'une commande dont un champ tableau a un éditeur de lignes (ADR-136 §2 ; view-body 3.5.0,
 * view-resolution 2.3.0). Composant générique : il ne connaît ni comptage ni article, seulement les
 * colonnes, la clé, le pré-remplissage par une requête nommée et les conditions que la vue déclare.
 *
 * - Les lignes viennent de la requête gouvernée (`prefill`), relancée quand un de ses paramètres
 *   change ; une saisie déjà faite reste attachée à sa clé.
 * - Une quantité reste du texte exact jusqu'à l'envoi ; une ligne vide n'est jamais un zéro.
 * - Un instant se saisit au fuseau que la réponse rend (site, sinon tenant), affiché à côté du champ.
 * - Ce qui empêche l'envoi se voit d'avance, avec sa raison (refus annoncé par la feuille, lignes sans
 *   valeur, clé en double, borne du nombre de lignes). Le serveur revalide et réautorise tout.
 * - Un brouillon local garde la saisie entre deux ouvertures ; il est effacé quand la commande est acceptée.
 * - La clé d'idempotence est créée une fois et réutilisée tant que rien n'est modifié : un renvoi
 *   après une coupure ne crée pas une seconde commande (ERP-IDEMP-001).
 */

type Field = ViewCommand['payload'][number];

function summaryOf(definition: Field): FieldSummary {
  const field = inputFieldOf(definition);
  const summary = { field_id: definition.field_id, label: definition.label ?? { fr: definition.field_id }, type: definition.type } as FieldSummary;
  if (field.enum_labels !== undefined) summary.enum_labels = field.enum_labels as Record<string, Record<string, string>>;
  if (definition.unit !== undefined) summary.unit = definition.unit;
  return summary;
}

const newKey = (): string => `web-${globalThis.crypto.randomUUID()}`;
const isEmpty = (value: unknown): boolean => value === undefined || value === null || value === '';

interface Refusal {
  category: string;
  code: string;
  message: string;
  fields: Array<{ field: string; message: string }>;
}

export function LineEditorDialog({
  command,
  spec,
  prefilled,
  target,
  expectedVersion,
  onDone,
  onClose,
  intro,
  draftStore,
}: {
  command: ViewCommand;
  spec: LineEditorSpec;
  prefilled: Readonly<Record<string, unknown>>;
  target: { type: string; id: string } | null;
  expectedVersion?: number;
  onDone: () => void;
  onClose: () => void;
  intro?: ReactNode;
  draftStore?: DraftStore;
}) {
  const services = useAppServices();
  const store = useMemo(() => draftStore ?? browserDraftStore(), [draftStore]);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [lines, setLines] = useState<EditorLine[]>([]);
  const [sheet, setSheet] = useState<{ status: 'none' | 'loading' | 'ready' | 'error'; data?: unknown; error?: string }>({ status: 'none' });
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [saving, setSaving] = useState(false);
  const [restored, setRestored] = useState<{ key: string; age: string } | null>(null);
  const idempotency = useRef<string>(newKey());
  const draftChecked = useRef<string | null>(null);

  const payload = useMemo(() => ({ ...values, ...prefilled }), [values, prefilled]);
  const scope = useMemo(() => ({ payload, context: services.context }), [payload, services.context]);
  const parameters = useMemo(() => prefillParameters(spec, scope), [spec, scope]);
  const parameterKey = JSON.stringify(parameters);
  const prefill = spec.prefill;
  const timeZone = prefill?.time_zone_path === undefined ? undefined : valueAt(sheet.data, prefill.time_zone_path);
  const zone = typeof timeZone === 'string' && timeZone !== '' ? timeZone : undefined;
  const blocking = useMemo(() => {
    const raw = prefill?.blocking_path === undefined ? undefined : valueAt(sheet.data, prefill.blocking_path);
    return raw !== null && typeof raw === 'object' && typeof (raw as Row)['message'] === 'string' ? { code: String((raw as Row)['code'] ?? ''), message: String((raw as Row)['message']) } : null;
  }, [prefill, sheet.data]);
  const tenant = String(services.context['tenant_id'] ?? '');
  const identity = String(services.context['identity_id'] ?? '');
  const sheetIdentity = identityParameters(spec, parameters, Object.fromEntries(command.payload.map((field) => [field.field_id, field.type])));
  const key = Object.keys(sheetIdentity).length === 0 && prefill !== undefined ? null : draftKey({ tenant, identity, command: command.command_name, sheet: sheetIdentity });

  /** Toute modification rend la saisie différente : une nouvelle clé d'idempotence la portera. */
  const touched = (): void => {
    idempotency.current = newKey();
  };

  // Lecture de la feuille (requête nommée), relancée quand un paramètre change.
  useEffect(() => {
    if (prefill === undefined) return;
    if (Object.keys(parameters).length === 0) {
      setSheet({ status: 'none' });
      setLines((current) => current.filter((line) => !line.prefilled));
      return;
    }
    let alive = true;
    setSheet((current) => ({ ...current, status: 'loading' }));
    services.client.query(prefill.query_name, parameters).then(
      (envelope) => {
        if (!alive) return;
        const data = envelope.data;
        setSheet({ status: 'ready', data });
        setLines((current) => mergePrefill(spec, prefilledRows(spec, data), current));
        setValues((current) => {
          let next = current;
          for (const [field, path] of Object.entries(prefill.defaults ?? {})) {
            const value = valueAt(data, path);
            if (isEmpty(next[field]) && !Object.hasOwn(prefilled, field) && !isEmpty(value)) next = { ...next, [field]: value };
          }
          return next;
        });
      },
      (error: unknown) => {
        if (!alive) return;
        setSheet({ status: 'error', error: error instanceof ApiError ? error.message : String(error) });
        setLines((current) => current.filter((line) => !line.prefilled));
      },
    );
    return () => {
      alive = false;
    };
    // `parameterKey` résume les paramètres : une même feuille n'est pas relue.
  }, [parameterKey]);

  // Reprise du brouillon, une fois par feuille, après la première lecture.
  useEffect(() => {
    if (key === null || draftChecked.current === key || (prefill !== undefined && sheet.status !== 'ready')) return;
    draftChecked.current = key;
    const draft = store.load(key);
    if (draft === null) return;
    setValues((current) => {
      const next = { ...current };
      for (const [field, value] of Object.entries(draft.values)) if (!Object.hasOwn(prefilled, field)) next[field] = value;
      return next;
    });
    setLines((current) => {
      const byKey = new Map(draft.lines.filter((line) => line.prefilled).map((line) => [lineKey(spec, line.data), line]));
      const merged = current.map((line) => (line.prefilled && byKey.has(lineKey(spec, line.data)) ? { ...line, entered: { ...byKey.get(lineKey(spec, line.data))!.entered } } : line));
      const added = draft.lines.filter((line) => !line.prefilled).map((line) => ({ id: line.id, data: line.data, entered: line.entered, prefilled: false }));
      return [...merged.filter((line) => line.prefilled), ...added];
    });
    setRestored({ key, age: draftAge(draft.saved_at) });
  }, [key, sheet.status, store, spec, prefill, prefilled]);

  // Sauvegarde du brouillon : seulement quand quelque chose a été saisi.
  useEffect(() => {
    if (key === null || draftChecked.current !== key) return;
    const typed = lines.some((line) => !line.prefilled || Object.values(line.entered).some((entry) => entry.trim() !== ''));
    if (!typed) return;
    const draft: EditorDraft = {
      saved_at: new Date().toISOString(),
      values,
      lines: lines.map((line) => ({ id: line.id, data: line.prefilled ? Object.fromEntries(spec.key.map((path) => [path, valueAt(line.data, path)])) : line.data, entered: line.entered, prefilled: line.prefilled })),
    };
    const asOf = valueAt(sheet.data, 'as_of');
    if (typeof asOf === 'string') draft.as_of = asOf;
    store.save(key, draft);
  }, [key, lines, values, sheet.data, spec, store]);

  const status = useMemo(() => editorStatus(spec, { lines, scope, blocking, prefill: prefill === undefined ? 'none' : sheet.status }), [spec, lines, scope, blocking, prefill, sheet.status]);
  const instantFields = new Set(prefill?.instant_fields ?? []);
  const reasons = [...status.reasons];
  if (prefill !== undefined && sheet.status === 'none' && lines.length === 0) reasons.unshift('Renseigner d’abord les champs qui déterminent les lignes.');
  for (const field of command.payload) {
    if (!instantFields.has(field.field_id)) continue;
    const value = payload[field.field_id];
    if (typeof value === 'string' && Date.parse(value) > Date.now()) reasons.unshift(`« ${text(field.label, 'fr', field.field_id)} » est dans le futur.`);
  }

  const submit = async (): Promise<void> => {
    if (reasons.length > 0) return;
    setSaving(true);
    setRefusal(null);
    try {
      const body: Record<string, unknown> = {};
      for (const [field, value] of Object.entries(payload)) if (!isEmpty(value)) body[field] = value;
      body[spec.payload_field] = status.payloadLines;
      const input: Parameters<typeof services.client.command>[1] = { payload: body, idempotency_key: idempotency.current };
      if (target !== null) input.target = target;
      if (expectedVersion !== undefined) input.expected_version = expectedVersion;
      await services.client.command(command.command_name, input);
      if (key !== null) store.clear(key);
      onDone();
    } catch (caught) {
      if (caught instanceof ApiError) setRefusal({ category: caught.category, code: caught.code, message: caught.message, fields: caught.fieldErrors.map((entry) => ({ field: entry.field.replace(/^payload\./, ''), message: entry.message })) });
      else setRefusal({ category: 'INTERNAL_ERROR', code: 'CLIENT_ERROR', message: String(caught), fields: [] });
    } finally {
      setSaving(false);
    }
  };

  const changeValue = (field: string, value: unknown): void => {
    touched();
    setValues((current) => ({ ...current, [field]: value }));
  };
  const changeLines = (next: EditorLine[]): void => {
    touched();
    setLines(next);
  };
  const forgetDraft = (): void => {
    if (key !== null) store.clear(key);
    touched();
    setRestored(null);
    setLines((current) => current.filter((line) => line.prefilled).map((line) => ({ ...line, entered: {} })));
  };

  const denied = refusal !== null && (refusal.category === 'AUTHORIZATION_ERROR' || refusal.category === 'POLICY_REJECTION');
  const fieldError = (field: string): string | undefined => refusal?.fields.find((entry) => entry.field === field)?.message;
  return (
    <Dialog title={text(command.label)} open onClose={onClose} size="lg">
      <form
        className="ay-form ay-line-editor-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {intro}
        {refusal !== null && (
          <div className={`ay-banner ${denied ? 'ay-banner--warning' : 'ay-banner--danger'}`} role="alert">
            <strong>{denied ? 'Action refusée' : 'Refusé par le serveur'}</strong>
            {` : ${refusal.message}`}
            {refusal.code !== '' && refusal.code !== refusal.category && <span className="ay-line-editor__code">{refusal.code}</span>}
            {refusal.fields.filter((entry) => entry.field.startsWith(`${spec.payload_field}.`)).length > 0 && (
              <ul className="ay-line-editor__field-errors">
                {refusal.fields
                  .filter((entry) => entry.field.startsWith(`${spec.payload_field}.`))
                  .slice(0, 10)
                  .map((entry) => (
                    <li key={entry.field}>{`Ligne ${lineNumber(entry.field, spec.payload_field)} : ${entry.message}`}</li>
                  ))}
              </ul>
            )}
          </div>
        )}
        {restored !== null && restored.key === key && (
          <p className="ay-banner ay-banner--info" role="status">
            {`Brouillon repris, enregistré ${restored.age} sur cet appareil.`}{' '}
            <button type="button" className="ay-link-button" onClick={forgetDraft}>
              Effacer le brouillon
            </button>
          </p>
        )}
        <div className="ay-line-editor__fields">
          {command.payload
            .filter((definition) => definition.field_id !== spec.payload_field)
            .map((definition) => {
              const id = definition.field_id;
              if (Object.hasOwn(prefilled, id)) {
                return (
                  <div key={id} className="ay-field">
                    <span className="ay-field__label">{text(definition.label, 'fr', id)}</span>
                    <output className="ay-field__readonly">{formatValue(prefilled[id], summaryOf(definition))}</output>
                  </div>
                );
              }
              if (instantFields.has(id)) {
                return <ZonedInstantInput key={id} definition={definition} value={values[id]} zone={zone} onChange={(value) => changeValue(id, value)} disabled={saving} error={fieldError(id)} />;
              }
              const props: Parameters<typeof FieldInput>[0] = { field: inputFieldOf(definition), value: values[id] ?? null, onChange: (value) => changeValue(id, value), disabled: saving };
              const message = fieldError(id);
              if (message !== undefined) props.error = message;
              return <FieldInput key={id} {...props} />;
            })}
        </div>
        {blocking !== null && (
          <p className="ay-banner ay-banner--warning" role="alert">
            <strong>Saisie impossible pour l’instant</strong>
            {` : ${blocking.message}`}
          </p>
        )}
        {sheet.status === 'error' && (
          <p className="ay-banner ay-banner--warning" role="alert">
            {`Les lignes n’ont pas pu être lues : ${sheet.error ?? ''}`}
          </p>
        )}
        <LineEditorTable spec={spec} lines={lines} onLines={changeLines} status={status} scope={scope} disabled={saving} loading={sheet.status === 'loading' && lines.length === 0} />
        <div className="ay-form__actions ay-line-editor__actions">
          {reasons.length > 0 && (
            <ul className="ay-line-editor__reasons" role="status" aria-label="Envoi impossible">
              {reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
          <span className="ay-line-editor__summary">{`${status.payloadLines.length} ligne(s) à envoyer`}</span>
        </div>
        <DialogActions cancel={{ label: 'Annuler', onClick: onClose, disabled: saving }} primary={{ label: 'Valider', type: 'submit', busy: saving, disabled: reasons.length > 0, ...(reasons[0] === undefined ? {} : { title: reasons[0] }) }} />
      </form>
    </Dialog>
  );
}

function lineNumber(field: string, payloadField: string): string {
  const match = new RegExp(`^${payloadField}\\.(\\d+)`).exec(field);
  return match === null ? '?' : String(Number.parseInt(match[1]!, 10) + 1);
}

/** Saisie d'un instant au fuseau du site (R1) : le fuseau s'affiche à côté du champ, jamais celui du navigateur. */
function ZonedInstantInput({ definition, value, zone, onChange, disabled, error }: { definition: Field; value: unknown; zone: string | undefined; onChange: (value: string | null) => void; disabled: boolean; error: string | undefined }) {
  const id = `input-${definition.field_id}`;
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (zone === undefined ? '' : instantToZonedInput(value, zone));
  return (
    <div className="ay-field">
      <label className="ay-field__label" htmlFor={id}>
        {text(definition.label, 'fr', definition.field_id)}
        {definition.required === true && <span aria-hidden="true"> *</span>}
      </label>
      <span className="ay-field__control">
        <input
          id={id}
          type="datetime-local"
          value={shown}
          disabled={disabled || zone === undefined}
          aria-invalid={error !== undefined}
          aria-describedby={`${id}-zone`}
          onChange={(event) => {
            setDraft(event.target.value);
            const instant = zone === undefined ? null : zonedInputToInstant(event.target.value, zone);
            if (instant !== null || event.target.value === '') {
              setDraft(null);
              onChange(instant);
            }
          }}
        />
        <span id={`${id}-zone`} className="ay-field__unit">
          {zone === undefined ? 'fuseau du site inconnu' : `heure de ${zone}`}
        </span>
      </span>
      {error !== undefined && <span className="ay-field__error">{error}</span>}
    </div>
  );
}

function referenceOf(value: unknown, column: LineEditorColumn): { type: string; id: string } | null {
  if (typeof value === 'string' && column.reference_type !== undefined) return { type: column.reference_type, id: value };
  if (value !== null && typeof value === 'object' && typeof (value as Row)['type'] === 'string' && typeof (value as Row)['id'] === 'string') return { type: (value as Row)['type'] as string, id: (value as Row)['id'] as string };
  return null;
}

const decimalShown = (value: string): string => formatDecimal(value, 'fr', { maximumFractionDigits: 15 });

/** Table de l'éditeur : mêmes colonnes que l'onglet `lines`, plus la saisie. */
export function LineEditorTable({
  spec,
  lines,
  onLines,
  status,
  scope,
  disabled,
  loading,
}: {
  spec: LineEditorSpec;
  lines: readonly EditorLine[];
  onLines: (next: EditorLine[]) => void;
  status: EditorStatus;
  scope: Readonly<Record<string, unknown>>;
  disabled: boolean;
  loading: boolean;
}) {
  const services = useAppServices();
  const [lookup, setLookup] = useState('');
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  const columns = visibleColumns(spec, lines);
  const decimals = columns.filter((column) => column.input === 'decimal');

  const wanted = useMemo(() => {
    const byType = new Map<string, Set<string>>();
    for (const line of lines) {
      for (const column of spec.columns) {
        if (column.format !== 'reference') continue;
        const ref = referenceOf(valueAt(line.data, column.path), column);
        if (ref !== null) byType.set(ref.type, (byType.get(ref.type) ?? new Set()).add(ref.id));
      }
    }
    return [...byType.entries()].map(([type, ids]) => ({ type, ids: [...ids] }));
  }, [lines, spec.columns]);
  const labels = useReferenceLabels(services.client, wanted);

  const update = (line: EditorLine, change: (current: EditorLine) => EditorLine): void => onLines(lines.map((entry) => (entry.id === line.id ? change(entry) : entry)));
  const focusLine = (line: EditorLine): void => {
    const first = decimals[0];
    const element = first === undefined ? null : document.getElementById(cellId(line, first));
    element?.focus();
    element?.scrollIntoView?.({ block: 'center' });
  };
  const focusLookup = (): void => document.getElementById('ay-line-lookup')?.focus();
  const addLine = (): void => {
    const line: EditorLine = { id: newLineId(), data: {}, entered: {}, prefilled: false };
    onLines([...lines, line]);
    setLookupMessage(null);
  };
  const runLookup = (): void => {
    const code = lookup.trim();
    if (code === '') return;
    const found = lookupLine(spec, lines, code);
    if (found !== undefined) {
      setLookup('');
      setLookupMessage(null);
      focusLine(found);
      return;
    }
    setLookupMessage(`« ${code} » n’est sur aucune ligne.`);
  };

  const display = (line: EditorLine, column: LineEditorColumn): ReactNode => {
    const value = valueAt(line.data, column.path);
    if (value === undefined || value === null) return '—';
    switch (column.format) {
      case 'reference': {
        const ref = referenceOf(value, column);
        return ref === null ? '—' : (labels.get(ref.id) ?? shortId(ref.id));
      }
      case 'enum':
        return typeof value === 'string' ? text(column.enum_labels?.[value], 'fr', value) : String(value);
      case 'decimal': {
        const exact = String(value);
        const unit = unitOf(spec, lines, line, column);
        return `${decimalShown(exact)}${unit === undefined ? '' : ` ${unit}`}`;
      }
      default:
        return typeof value === 'string' || typeof value === 'number' ? String(value) : JSON.stringify(value);
    }
  };

  const cell = (line: EditorLine, column: LineEditorColumn, index: number): ReactNode => {
    const errors = status.invalid.get(line.id) ?? {};
    const label = `${text(column.label)}, ligne ${index + 1}`;
    const isKey = spec.key.includes(column.path);
    if (column.input === 'decimal') {
      const unit = unitOf(spec, lines, line, column);
      const error = errors[column.path];
      const unitError = column.unit_path === undefined ? undefined : errors[column.unit_path];
      return (
        <span className="ay-line-editor__quantity">
          <input
            id={cellId(line, column)}
            className="ay-line-editor__input"
            inputMode="decimal"
            autoComplete="off"
            value={line.entered[column.path] ?? ''}
            aria-label={label}
            aria-invalid={error !== undefined || status.missing.has(line.id)}
            title={error}
            disabled={disabled}
            placeholder="—"
            onChange={(event) => update(line, (current) => ({ ...current, entered: { ...current.entered, [column.path]: event.target.value } }))}
            onKeyDown={(event) => {
              // Une douchette finit par Entrée : on revient à la recherche, on n'envoie jamais le formulaire.
              if (event.key === 'Enter') {
                event.preventDefault();
                focusLookup();
              }
            }}
          />
          {unit !== undefined ? (
            <span className="ay-field__unit">{unit}</span>
          ) : (
            column.unit_path !== undefined &&
            !line.prefilled && (
              <input
                className="ay-line-editor__unit"
                aria-label={`Unité, ligne ${index + 1}`}
                aria-invalid={unitError !== undefined}
                value={typeof valueAt(line.data, column.unit_path) === 'string' ? (valueAt(line.data, column.unit_path) as string) : ''}
                disabled={disabled}
                placeholder="unité"
                onChange={(event) => update(line, (current) => ({ ...current, data: setAt(current.data, column.unit_path!, event.target.value === '' ? undefined : event.target.value) }))}
              />
            )
          )}
          {error !== undefined && <span className="ay-line-editor__error">{error}</span>}
        </span>
      );
    }
    // Une ligne pré-remplie garde sa clé ; une ligne ajoutée la choisit.
    const editable = (column.input === 'reference' || column.input === 'enum') && !(line.prefilled && isKey);
    if (!editable) return display(line, column);
    if (column.input === 'enum') {
      const value = valueAt(line.data, column.path);
      return (
        <select aria-label={label} value={typeof value === 'string' ? value : ''} disabled={disabled} onChange={(event) => update(line, (current) => ({ ...current, data: setAt(current.data, column.path, event.target.value === '' ? undefined : event.target.value) }))}>
          <option value="">—</option>
          {Object.entries(column.enum_labels ?? {}).map(([value, entry]) => (
            <option key={value} value={value}>
              {text(entry, 'fr', value)}
            </option>
          ))}
        </select>
      );
    }
    return <LineReferencePicker column={column} line={line} scope={scope} label={label} error={errors[column.path]} disabled={disabled} onChange={(value) => update(line, (current) => ({ ...current, data: setAt(current.data, column.path, value) }))} />;
  };

  const totals = (spec.totals ?? []).flatMap((path) => {
    const column = columns.find((entry) => entry.path === path);
    return column === undefined ? [] : [{ path, text: columnTotal(spec, lines, column).map((entry) => `${decimalShown(entry.total)}${entry.unit === '' ? '' : ` ${entry.unit}`}`).join(' · ') }];
  });
  const filled = lines.filter((line) => decimals.length > 0 && decimals.every((column) => (line.entered[column.path] ?? '').trim() !== '')).length;

  return (
    <div className="ay-lines ay-line-editor">
      <div className="ay-lines__toolbar">
        {spec.lookup !== undefined && (
          <label className="ay-line-editor__lookup">
            <span>{text(spec.lookup.label, 'fr', 'Aller à la ligne')}</span>
            <input
              id="ay-line-lookup"
              type="search"
              autoComplete="off"
              value={lookup}
              disabled={disabled}
              placeholder="Code, ou scan"
              onChange={(event) => {
                setLookup(event.target.value);
                setLookupMessage(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  runLookup();
                }
              }}
            />
          </label>
        )}
        <span className="ay-lines__tally" role="status">
          {`${filled} / ${lines.length} ligne(s) saisie(s)`}
          {status.required ? ' · toutes obligatoires' : ''}
        </span>
      </div>
      {lookupMessage !== null && (
        <p className="ay-line-editor__lookup-message" role="status">
          {lookupMessage}{' '}
          {status.canAdd ? (
            <button type="button" className="ay-link-button" onClick={addLine}>
              Ajouter une ligne
            </button>
          ) : (
            'Aucune ligne ne peut être ajoutée ici.'
          )}
        </p>
      )}
      {spec.max_lines !== undefined && lines.length > spec.max_lines && (
        <p className="ay-banner ay-banner--warning">{`${lines.length} lignes : au-delà de ${spec.max_lines}, l’envoi est refusé. ${text(spec.overflow_hint, 'fr', '')}`.trim()}</p>
      )}
      <div className="ay-table-wrap ay-line-editor__table">
        <table className="ay-table">
          <thead>
            <tr>
              <th scope="col" className="ay-line-editor__index">
                #
              </th>
              {columns.map((column) => (
                <th key={column.path} scope="col" className={column.format === 'decimal' ? 'ay-col--end ay-num' : undefined}>
                  {text(column.label)}
                </th>
              ))}
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length + 2}>Lecture des lignes…</td>
              </tr>
            )}
            {!loading && lines.length === 0 && (
              <tr>
                <td colSpan={columns.length + 2} className="ay-text--muted">
                  Aucune ligne.
                </td>
              </tr>
            )}
            {lines.map((line, index) => {
              const marks = [status.duplicates.has(line.id) ? 'ay-line-editor__row--duplicate' : '', status.missing.has(line.id) ? 'ay-line-editor__row--missing' : '', status.invalid.has(line.id) ? 'ay-line-editor__row--invalid' : '', line.prefilled ? '' : 'ay-line-editor__row--added'].filter((entry) => entry !== '');
              return (
                <tr key={line.id} className={marks.join(' ') || undefined} data-line={index + 1}>
                  <td className="ay-line-editor__index ay-num">
                    {index + 1}
                    {status.duplicates.has(line.id) && <span className="ay-line-editor__flag">{' '}en double</span>}
                  </td>
                  {columns.map((column) => (
                    <td key={column.path} className={column.format === 'decimal' ? 'ay-col--end ay-num' : undefined}>
                      {cell(line, column, index)}
                    </td>
                  ))}
                  <td>
                    {!line.prefilled && (
                      <button type="button" className="ay-link-button" disabled={disabled} onClick={() => onLines(lines.filter((entry) => entry.id !== line.id))}>
                        Retirer
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {totals.length > 0 && lines.length > 0 && (
            <tfoot>
              <tr>
                <td />
                {columns.map((column, position) => {
                  const total = totals.find((entry) => entry.path === column.path);
                  return (
                    <td key={column.path} className={column.format === 'decimal' ? 'ay-col--end ay-num' : undefined}>
                      {position === 0 ? <strong>{`Total · ${lines.length} ligne(s)`}</strong> : total === undefined ? null : <strong>{total.text}</strong>}
                    </td>
                  );
                })}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {status.canAdd && (
        <div>
          <button type="button" className="ay-button ay-button--secondary" onClick={addLine} disabled={disabled}>
            Ajouter une ligne
          </button>
        </div>
      )}
    </div>
  );
}

const cellId = (line: EditorLine, column: LineEditorColumn): string => `le-${line.id}-${column.path.replace(/\./g, '-')}`;

type Listed = Record<string, unknown> & { id?: string };

/** Choix d'une référence dans la liste gouvernée de son type, filtrée par `reference_filter` sur `$line`. */
function LineReferencePicker({ column, line, scope, label, error, disabled, onChange }: { column: LineEditorColumn; line: EditorLine; scope: Readonly<Record<string, unknown>>; label: string; error: string | undefined; disabled: boolean; onChange: (value: unknown) => void }) {
  const services = useAppServices();
  const type = column.reference_type ?? '';
  const [rows] = useLoad(`line-reference:${type}`, () => (type === '' ? Promise.resolve({ data: [] as Listed[] }) : services.client.listObjects<Listed>(type, { limit: 200 })));
  const filters = Object.entries(column.reference_filter ?? {}).map(([field, expression]) => ({ field, wanted: evaluate(expression, { ...scope, line: line.data }) }));
  const waiting = filters.some((filter) => filter.wanted === null || filter.wanted === undefined || filter.wanted === '');
  const current = referenceOf(valueAt(line.data, column.path), column);
  if (rows.status === 'loading') return <select aria-label={label} disabled aria-busy="true"><option>Chargement…</option></select>;
  if (rows.status === 'error') return <span className="ay-field__error">{rows.error.message}</span>;
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const options = waiting ? [] : rows.value.data.filter((row) => typeof row.id === 'string' && filters.every((filter) => same(valueAt(row, filter.field), filter.wanted)));
  return (
    <span className="ay-line-editor__picker">
      <select aria-label={label} aria-invalid={error !== undefined} title={error} disabled={disabled || waiting} value={current?.id ?? ''} onChange={(event) => onChange(event.target.value === '' ? undefined : referenceValue(column, event.target.value))}>
        <option value="">{waiting ? 'Choisir d’abord la ligne' : '—'}</option>
        {options.map((row) => (
          <option key={row.id} value={row.id}>
            {String(row['display_name'] ?? row['lot_code'] ?? row['code'] ?? row['reference'] ?? row.id)}
          </option>
        ))}
      </select>
    </span>
  );
}
